import React, { useState } from "react";
import axios from "axios";
import { API_BASE } from "../../config/api";
import "./LoginSignup.css";
import { useNavigate } from "react-router-dom";
import TermsAndConditions from '../../pages/TermsAndConditions';
import { sendSMSOtp } from "../../utils/smsOtp";

const Register = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const navigate = useNavigate();
  const [showTerms, setShowTerms] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [checkboxEnabled, setCheckboxEnabled] = useState(false);
  const [otpMethod, setOtpMethod] = useState("email");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const normalizePhone = (phone) => {
    const digits = String(phone || "").replace(/\D/g, "");

    if (digits.length === 10) {
      return digits;
    }

    if (digits.length === 12 && digits.startsWith("91")) {
      return digits.slice(2);
    }

    throw new Error("Please enter a valid 10-digit phone number.");
  };

  const clearPendingAuth = () => {
    localStorage.removeItem("pendingEmail");
    localStorage.removeItem("pendingPhone");
    localStorage.removeItem("pendingOtpPurpose");
    localStorage.removeItem("pendingOtpMethod");
    localStorage.removeItem("pendingRegistrationData");
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    const { name, email, phone, password } = formData;
    if (!name || !email || !phone || !password) {
      return setError("All fields are required.");
    }
    if (!acceptedTerms) {
      setError('You must accept the Terms and Conditions.');
      return;
    }

    // Start loading state
    setIsSubmitting(true);
    setError("");
    setLoadingMessage(
      otpMethod === "sms"
        ? "Verifying your phone and preparing account setup..."
        : "Creating your account and sending OTP..."
    );

    try {
      // Add small delay for better UX
      await new Promise(resolve => setTimeout(resolve, 300));

      clearPendingAuth();

      if (otpMethod === "sms") {
        const normalizedPhone = normalizePhone(formData.phone);
        await sendSMSOtp(normalizedPhone, "register");

        localStorage.setItem("pendingPhone", normalizedPhone);
        localStorage.setItem("pendingOtpPurpose", "register");
        localStorage.setItem("pendingOtpMethod", "sms");
        localStorage.setItem(
          "pendingRegistrationData",
          JSON.stringify({
            ...formData,
            email: formData.email.trim().toLowerCase(),
            phone: normalizedPhone,
          })
        );
      } else {
        await axios.post(`${API_BASE}/v1/user/send-otp`, {
          ...formData,
          purpose: "register",
        });

        localStorage.setItem("pendingEmail", formData.email.trim().toLowerCase());
        localStorage.setItem("pendingOtpPurpose", "register");
        localStorage.setItem("pendingOtpMethod", "email");
      }
      
      // Add small delay before navigation for smooth transition
      setTimeout(() => {
        navigate("/verify-otp");
      }, 500);
      
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Registration failed");
    } finally {
      // Stop loading state
      setIsSubmitting(false);
      setLoadingMessage("");
    }
  };

  const openTerms = () => {
    setShowTerms(true);
    setCheckboxEnabled(false);
    setAcceptedTerms(false);
  };
  const handleDone = () => {
    setShowTerms(false);
    setCheckboxEnabled(true);
  };

  return (
    <div className="auth-container">
      <div className="auth-box">
        <h2 className="auth-title">Create Account</h2>
        <div className="otp-method-switch" role="tablist" aria-label="Choose OTP method">
          <button
            type="button"
            className={`otp-method-button ${otpMethod === "email" ? "active" : ""}`}
            onClick={() => {
              setOtpMethod("email");
              setError("");
            }}
          >
            Email OTP
          </button>
          <button
            type="button"
            className={`otp-method-button ${otpMethod === "sms" ? "active" : ""}`}
            onClick={() => {
              setOtpMethod("sms");
              setError("");
            }}
          >
            SMS OTP
          </button>
        </div>
        <form onSubmit={handleRegister}>
          <input
            type="text"
            name="name"
            placeholder="Full Name"
            value={formData.name}
            onChange={handleChange}
            required
          />
          <input
            type="email"
            name="email"
            placeholder="Email Address"
            value={formData.email}
            onChange={handleChange}
            required
          />
          <input
            type="tel"
            name="phone"
            placeholder="Phone Number (10 digits)"
            value={formData.phone}
            onChange={handleChange}
            required
          />
          <input
            type="password"
            name="password"
            placeholder="Create Password"
            value={formData.password}
            onChange={handleChange}
            required
          />
          
          {/* ✅ Verification Method Dropdown */}
          <div className="terms-row">
            <input
              type="checkbox"
              id="terms"
              checked={acceptedTerms}
              onChange={e => setAcceptedTerms(e.target.checked)}
              required
              disabled={!checkboxEnabled}
            />
            <label htmlFor="terms">
              I accept the{' '}
              <span
                className="terms-link"
                onClick={e => { e.preventDefault(); openTerms(); }}
              >
                Terms and Conditions
              </span>
            </label>
          </div>

          {error && <p className="auth-error">{error}</p>}
          <button 
            type="submit" 
            className={`auth-button ${isSubmitting ? 'auth-button-loading' : ''}`}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span className="button-content">
                <span className="spinner"></span>
                Creating Account...
              </span>
            ) : (
              otpMethod === "sms" ? "Send SMS OTP" : "Register"
            )}
          </button>
        </form>
        <div className="auth-footer">
          <p>
            Already have an account?{" "}
            <span onClick={() => navigate("/login")}>Login</span>
          </p>
        </div>
      </div>
      
      {/* Loading Overlay */}
      {isSubmitting && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <p className="loading-message">{loadingMessage}</p>
          </div>
        </div>
      )}
      
      {showTerms && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ background: '#fff', borderRadius: '8px', maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 2px 16px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '1rem' }}>
              <button onClick={() => setShowTerms(false)} style={{ fontSize: '1.2rem', background: 'none', border: 'none', cursor: 'pointer' }}>&times;</button>
            </div>
            <TermsAndConditions onDone={handleDone} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Register;
